import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  HealthSummaryResponse,
  NutritionSummaryListResponse,
  NutritionSummaryDetailResponse,
  NutritionSummaryDetailBloodSugarResponse,
  FeaturedFood,
  NutritionSummaryResponse,
  IntroQuestionnaireResponse,
  SubmitQuestionnaireData,
  PlantVirusData,
  JourneyStateResponse,
  PeekIntoResponse,
  TestStatus,
} from './Home.model';
import { home } from '../../api/Endpoints';
import { QUERY_KEYS } from '../../api/config';

export const useGetHealthSummary = () => {
  return useQuery<HealthSummaryResponse, Error>({
    queryKey: [QUERY_KEYS.HEALTH_SUMMARY],
    queryFn: async () => {
      const response = await home.getHealthSummary();
      return response.data;
    },
  });
};

export const useGetNutritionSummaryList = () => {
  return useQuery<NutritionSummaryListResponse, Error>({
    queryKey: [QUERY_KEYS.NUTRITION_SUMMARY_LIST],
    queryFn: async () => {
      const response = await home.getNutritionSummaryList();
      return response.data;
    },
  });
};

export const useGetNutritionSummaryDetail = (scoreId: number) => {
  return useQuery<NutritionSummaryDetailResponse, Error>({
    queryKey: [QUERY_KEYS.NUTRITION_SUMMARY_DETAIL, scoreId],
    queryFn: async () => {
      const response = await home.getNutritionSummaryDetail(scoreId);
      return response.data;
    },
  });
};

export const useGetNutritionSummaryDetailBloodSugar = () => {
  return useQuery<NutritionSummaryDetailBloodSugarResponse, Error>({
    queryKey: [QUERY_KEYS.NUTRITION_SUMMARY_DETAIL_BLOOD_SUGAR],
    queryFn: async () => {
      const response = await home.getNutritionSummaryDetailBloodSugar();
      return response.data;
    },
  });
};

export const useGetFeaturedFood = () => {
  return useQuery<FeaturedFood, Error>({
    queryKey: [QUERY_KEYS.FEATURED_FOOD],
    queryFn: async () => {
      const response = await home.getFeaturedFood();
      return response.data;
    },
  });
};

export const useGetNutritionSummary = () => {
  return useQuery<NutritionSummaryResponse, Error>({
    queryKey: [QUERY_KEYS.NUTRITION_SUMMARY],
    queryFn: async () => {
      const response = await home.getNutritionSummary();
      return response.data;
    },
  });
};

export const useGetKitStatus = () => {
  return useQuery<TestStatus, Error>({
    queryKey: [QUERY_KEYS.KIT_STATUS],
    queryFn: async () => {
      const response = await home.getkitStatus();
      return response.data;
    },
  });
};

export const useGetIntroQuestionnaires = () => {
  return useQuery<IntroQuestionnaireResponse, Error>({
    queryKey: [QUERY_KEYS.INTRO_QUESTIONNAIRES],
    queryFn: async () => {
      const response = await home.getIntroQuestionnaires();
      return response.data;
    },
  });
};

export const useIntroQuestionnaireMutation = (
  options?: Partial<UseMutationOptions<null, Error, SubmitQuestionnaireData>>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data: SubmitQuestionnaireData) => {
      return home.submitIntroQuestionnaire(data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.INTRO_QUESTIONNAIRES],
      });
    },
  });
};

export const useGetPlantVirusData = () => {
  return useQuery<PlantVirusData, Error>({
    queryKey: [QUERY_KEYS.PLANT_VIRUS],
    queryFn: async () => {
      const response = await home.getPlantVirusData();
      return response.data;
    },
  });
};

export const useGetJourneyState = () => {
  return useQuery<JourneyStateResponse, Error>({
    queryKey: [QUERY_KEYS.JOURNEY_STATES],
    queryFn: async () => {
      const response = await home.getJourneyStates();
      return response.data;
    },
  });
};

export const useGetPeekIntoData = (scoreId: number) => {
  return useQuery<PeekIntoResponse, Error>({
    queryKey: [QUERY_KEYS.PEEK_INTO_DATA, scoreId],
    queryFn: async () => {
      const response = await home.getPeekIntoData(scoreId);
      return response.data;
    },
  });
};
